/**
 * @file Componente AutoForm do EP.
 *
 * AutoForm constrói formulários a partir de configuração local ou contratos
 * retornados pelo backend. Suporta modos `new`, `edit`, `view` e `filter`, uso
 * inline ou em Dialog, binding de dados, composites, DataGrid/MultiList,
 * validação, save customizado e callbacks.
 *
 * O destino técnico é normalizado por `normalizeServiceTarget()` e o método da
 * operação é sempre definido internamente pelo AutoForm.
 *
 * @module components/create/autoform
 * @see module:components/create/element
 * @see normalizeServiceTarget
 */

import ajax from "../../tools/ajax";
import Format from "../../tools/format";
import msgbox from "../../tools/msgbox";
import toast from "../../tools/toast";
import session from "../../tools/session";
import Loader from "../../tools/loader";
import { resolveBackendErrorMessage, resolveBackendErrorType } from "../../tools/backendError";
import { getServiceTargetError, normalizeServiceTarget } from "../../tools/serviceTarget.js";
import { applyAutoformFieldModes } from "../../tools/autoformFieldModes.js";
import { resolveNestedServiceContext } from "../../tools/nestedServiceContext.js";

const format = new Format();
let autoformSeq = 0;
const allowUnsafeEval = String(
  (typeof process !== 'undefined' && process?.env?.ALLOW_UNSAFE_EVAL) || ''
).trim() === '1';

/**
 * Detecta texto que aparenta ser uma função/arrow function serializada.
 *
 * @param {*} [value=''] Valor candidato.
 * @returns {boolean} `true` quando o texto possui assinatura de função.
 */
const looksLikeFunctionSource = (value = '') => {
  const normalized = String(value || '').trim();
  return /^(?:async\s+)?function(?:\s+[A-Za-z_$][\w$]*)?\s*\(/.test(normalized)
    || /^(?:async\s+)?(?:\([^)]*\)|[A-Za-z_$][\w$]*)\s*=>/.test(normalized);
};

/**
 * Controller runtime do AutoForm.
 *
 * @class
 */
class AutoForm {
  #getFormControls(form) {
    if (!form || typeof form.querySelectorAll !== "function") return [];
    return Array.from(form.querySelectorAll("input, select, textarea"))
      .filter((element) => !element.closest?.(".datagrid-wrap"));
  }

  constructor() {
    this.configScreenCache = new Map();
  }

  /**
   * Normaliza e valida o target de uma operação interna.
   *
   * @param {Object} [options={}] Opções contendo o target.
   * @param {string} [method=''] Operação interna.
   * @returns {Object} Target validado.
   * @throws {Error} Quando o target é inválido/incompleto.
   * @see normalizeServiceTarget
   */
  #getServiceTarget(options = {}, method = "") {
    const target = normalizeServiceTarget({
      ...options,
      method,
    });
    const error = getServiceTargetError(target);

    if (error) {
      throw new Error(`autoform: ${error}`);
    }

    return target;
  }

  /**
   * Executa POST para uma operação interna do target do AutoForm.
   *
   * @param {Object} [options={}] Opções/target.
   * @param {string} [method=''] Operação.
   * @param {*} [body=null] Payload.
   * @param {Object|null} [requestConfig=null] Configuração Ajax.
   * @returns {Promise<*>} Resposta do Ajax.
   * @see normalizeServiceTarget
   */
  async #post(options = {}, method = "", body = null, requestConfig = null) {
    const target = this.#getServiceTarget(options, method);
    const config = (requestConfig && typeof requestConfig === "object")
      ? requestConfig
      : undefined;
    return ajax.post({
      ...target,
      body,
      ...(config ? { config } : {}),
    });
  }

  /**
   * Resolve uma referência textual `dic.*` contra `window.dic`.
   *
   * @param {*} value Valor/referência.
   * @returns {*} Valor localizado ou original.
   */
  #resolveDicRef(value) {
    if (typeof value !== "string") return value;
    const raw = value.trim();
    const unquoted = raw.replace(/^[\'"`\s]+|[\'"`\s]+$/g, "");
    if (!/^dic\./i.test(unquoted)) return value;

    const parts = unquoted.replace(/^dic\./i, "").split(".").filter(Boolean);
    let cursor = window?.dic;

    for (const part of parts) {
      if (!cursor || typeof cursor !== "object") return value;
      if (Object.prototype.hasOwnProperty.call(cursor, part)) {
        cursor = cursor[part];
        continue;
      }
      const fallbackKey = Object.keys(cursor).find(
        (k) => String(k).toLowerCase() === part.toLowerCase()
      );
      if (!fallbackKey) return value;
      cursor = cursor[fallbackKey];
    }

    if (typeof cursor === "string" || typeof cursor === "number") {
      return String(cursor);
    }

    return value;
  }

  /**
   * Resolve recursivamente referências `dic.*` em arrays/objetos.
   *
   * @param {*} node Nó de configuração.
   * @returns {*} Estrutura clonada/resolvida.
   */
  #resolveDicDeep(node) {
    if (Array.isArray(node)) {
      return node.map((item) => this.#resolveDicDeep(item));
    }
    if (node && typeof node === "object") {
      const out = {};
      for (const [key, value] of Object.entries(node)) {
        out[key] = this.#resolveDicDeep(value);
      }
      return out;
    }
    return this.#resolveDicRef(node);
  }

  /**
   * Resolve uma mensagem backend priorizando mensagem explícita, detalhes,
   * códigos localizados e fallbacks conhecidos de autenticação.
   *
   * @param {Object} [result={}] Resposta/erro backend.
   * @param {Object} [options={}] Contexto de dicionário.
   * @returns {string|null} Mensagem localizada.
   */
  #resolveBackendErrorMessage(result = {}, options = {}) {
    return resolveBackendErrorMessage(result, options);
  }

  /**
   * Persiste linhas pendentes de DataGrids aninhados após salvar o registro pai.
   *
   * @param {HTMLFormElement} form Form corrente.
   * @param {Object|null} [parentRecord=null] Registro pai salvo.
   * @returns {Promise<{status:string,rows:number}|Object>} Resultado agregado ou primeiro erro.
   */
  async #flushNestedDatagrids(form, parentRecord = null) {
    if (!form || typeof form.querySelectorAll !== "function") {
      return { status: "success", rows: 0 };
    }

    const wraps = Array.from(form.querySelectorAll(".datagrid-wrap"));
    let rows = 0;

    for (const wrap of wraps) {
      const instance = wrap?.datagridInstance || null;
      if (!instance || typeof instance.flushPendingRows !== "function") continue;
      const result = await instance.flushPendingRows(parentRecord);
      if (result?.status !== "success") {
        return result || { status: "error", code: "PENDING_FLUSH_FAILED" };
      }
      rows += Number(result?.rows || 0);
    }

    const flushables = Array.from(
      form.querySelectorAll('[data-autoform-flushable="true"]')
    );

    for (const flushable of flushables) {
      if (typeof flushable.autoformFlushPending !== "function") continue;
      const result = await flushable.autoformFlushPending(parentRecord);
      if (result?.status !== "success") {
        return result || { status: "error", code: "PENDING_FLUSH_FAILED" };
      }
      rows += Number(result?.rows || 0);
    }

    return { status: "success", rows };
  }

  /**
   * Coleta configurações MultiList com persistência pai/filho habilitada.
   *
   * @param {Array<Object>} [groups=[]] Árvore de grupos.
   * @returns {Array<Object>} Campos MultiList encontrados.
   */
  #collectMultiListFields(groups = []) {
    const fields = [];
    /** @param {*} item Nó visitado. @returns {void} */
    const visit = (item) => {
      if (!item) return;
      if (Array.isArray(item)) {
        item.forEach(visit);
        return;
      }
      if (typeof item !== "object") return;
      if (item.element === "multiList" && item.id && item.save && item.saveParent) {
        fields.push(item);
      }
      if (Array.isArray(item.content)) visit(item.content);
      if (Array.isArray(item.groups)) visit(item.groups);
    };
    visit(groups);
    return fields;
  }

  /**
   * Lê valores runtime dos MultiLists persistíveis.
   *
   * @param {HTMLFormElement} form Form corrente.
   * @param {Object} [options={}] Opções/grupos.
   * @returns {Object<string,*>} Valores por id.
   */
  #collectMultiListData(form, options = {}) {
    const output = {};
    const fields = this.#collectMultiListFields(options.groups || []);

    for (const field of fields) {
      const id = String(field.id || "").trim();
      if (!id) continue;
      const selector = window.CSS?.escape
        ? `#${window.CSS.escape(id)}`
        : `#${id.replace(/(["\\#.;?+*~':!^$[\]()=>|/@])/g, "\\$1")}`;
      const elem = form?.querySelector?.(selector) || document.getElementById(id);
      if (!elem || typeof elem.getValue !== "function") continue;
      output[id] = elem.getValue();
    }

    return output;
  }

  /**
   * Coleta MultiLists marcados como obrigatórios.
   *
   * @param {Array<Object>} [groups=[]] Árvore de grupos.
   * @returns {Array<Object>} Campos obrigatórios.
   */
  #collectRequiredMultiListFields(groups = []) {
    const fields = [];
    /** @param {*} item Nó visitado. @returns {void} */
    const visit = (item) => {
      if (!item) return;
      if (Array.isArray(item)) {
        item.forEach(visit);
        return;
      }
      if (typeof item !== "object") return;
      if (item.element === "multiList" && item.id && item.required === true) {
        fields.push(item);
      }
      if (Array.isArray(item.content)) visit(item.content);
      if (Array.isArray(item.groups)) visit(item.groups);
    };
    visit(groups);
    return fields;
  }

  /**
   * Resolve elemento por id dentro do form ou documento com escape seguro.
   *
   * @param {HTMLFormElement} form Form corrente.
   * @param {string} [id=''] Id.
   * @returns {HTMLElement|null} Elemento.
   */
  #resolveElementById(form, id = "") {
    const safeId = String(id || "").trim();
    if (!safeId) return null;
    const selector = window.CSS?.escape
      ? `#${window.CSS.escape(safeId)}`
      : `#${safeId.replace(/(["\\#.;?+*~':!^$[\]()=>|/@])/g, "\\$1")}`;
    return form?.querySelector?.(selector) || document.getElementById(safeId);
  }

  /**
   * Indica se um MultiList possui ao menos uma seleção no target.
   *
   * @param {HTMLElement} elem MultiList runtime.
   * @returns {boolean} Estado de seleção.
   */
  #multiListHasSelection(elem) {
    if (!elem || typeof elem.getValue !== "function") return false;
    const value = elem.getValue();
    const target = value?.target;
    if (!target || typeof target !== "object") return false;

    return Object.values(target).some((columnValue) => {
      if (Array.isArray(columnValue)) return columnValue.length > 0;
      if (columnValue && typeof columnValue === "object" && Array.isArray(columnValue.data)) {
        return columnValue.data.length > 0;
      }
      return false;
    });
  }

  /**
   * Atualiza estado visual/dataset de validação de um MultiList.
   *
   * @param {HTMLElement} elem MultiList.
   * @param {boolean} isValid Estado.
   * @returns {void}
   */
  #setMultiListValidationState(elem, isValid) {
    if (!elem) return;
    elem.data?.("valid", Boolean(isValid));
    elem.classList.toggle("invalid", !isValid);
    if (!isValid) {
      elem.classList.remove("valid");
    }
  }

  /**
   * Resolve label usado em mensagens de validação de MultiList.
   *
   * @param {Object} field Configuração.
   * @param {HTMLElement} elem Elemento.
   * @returns {string} Label/id.
   */
  #resolveMultiListLabel(field, elem) {
    const labelText = elem?.querySelector?.(":scope > label")?.innerText?.trim();
    if (labelText) return labelText;
    if (typeof field?.label === "string" && field.label.trim()) return field.label.trim();
    return field?.id || "";
  }

  /**
   * Normaliza recursivamente um valor para comparação determinística de estado.
   *
   * Arrays preservam ordem. Objetos têm chaves ordenadas para que diferenças de
   * inserção não gerem falso dirty.
   *
   * @param {*} value Valor.
   * @returns {*} Valor normalizado.
   */
  #normalizeDirtyValue(value) {
    if (Array.isArray(value)) {
      return value.map((item) => this.#normalizeDirtyValue(item));
    }
    if (value && typeof value === "object") {
      return Object.keys(value)
        .sort()
        .reduce((acc, key) => {
          const normalized = this.#normalizeDirtyValue(value[key]);
          if (normalized !== undefined) acc[key] = normalized;
          return acc;
        }, {});
    }
    if (value === undefined) return null;
    return value;
  }

  /**
   * Cria snapshot canônico do estado editável do AutoForm.
   *
   * Além de inputs/selects/textareas, inclui MultiList e linhas pendentes de
   * DataGrids aninhados para que componentes compostos também participem do
   * dirty tracking.
   *
   * @param {HTMLFormElement} form Form corrente.
   * @returns {string} Snapshot serializado.
   */
  #getDirtySnapshot(form) {
    if (!form || typeof form.querySelectorAll !== "function") return "";

    const fields = {};
    const processed = new Set();
    const elements = this.#getFormControls(form);

    for (const el of elements) {
      const name = String(el?.name || "").trim();
      const isPhoneAcronym = name.endsWith("-acronym")
        && el.closest?.(".input-wrapper.phone");

      if (
        !name
        || el.disabled
        || processed.has(name)
        || el.dataset?.id === "ddi"
        || isPhoneAcronym
      ) {
        continue;
      }

      let value;
      if (el.dataset?.type === "otp-hidden") {
        const scope = el.closest?.(".fieldbox") || form;
        value = Array.from(scope.querySelectorAll('input[data-type="otp"]'))
          .map((input) => String(input.value || "").trim())
          .join("");
      } else if (el.type === "radio") {
        const selected = elements.find((candidate) => (
          candidate.type === "radio"
          && candidate.name === name
          && candidate.checked
        ));
        value = selected?.value ?? null;
      } else if (el.type === "checkbox") {
        const sameName = elements.filter((candidate) => (
          candidate.type === "checkbox" && candidate.name === name
        ));
        const selected = sameName.filter((candidate) => candidate.checked);
        if (sameName.length > 1) {
          value = selected.map((candidate) => candidate.value);
        } else {
          value = selected.length === 1
            ? (selected[0].value === "on" ? true : selected[0].value)
            : false;
        }
      } else if (el.tagName === "SELECT" && el.multiple) {
        value = Array.from(el.selectedOptions || []).map((option) => option.value);
      } else {
        value = el.value;
        if (el.dataset?.type === "phone") {
          const isNational = el.dataset.national === "true";
          const scope = el.closest?.(".fieldbox") || form;
          const ddiInput = scope?.querySelector?.(`[name="${name}-ddi"]`)
            || form.querySelector?.(`[name="${name}-ddi"]`);
          const ddiRaw = ddiInput?.value || el.dataset.ddi || "";
          const ddiDigits = String(ddiRaw).replace(/\D/g, "");
          const unmasked = typeof el.getUnmasked === "function"
            ? el.getUnmasked()?.value
            : el.value;
          let digits = String(unmasked || "").replace(/\D/g, "");
          if (!isNational && ddiDigits && digits.startsWith(ddiDigits)) {
            digits = digits.slice(ddiDigits.length);
          }
          value = digits.length > 0 ? `${ddiDigits}${digits}` : "";
        }
      }

      processed.add(name);
      fields[name] = value;
    }

    const multiLists = {};
    form.querySelectorAll(".multi-list").forEach((elem, index) => {
      if (typeof elem.getValue !== "function") return;
      const key = String(elem.id || ("multiList:" + index));
      multiLists[key] = elem.getValue();
    });

    const datagrids = {};
    form.querySelectorAll(".datagrid-wrap").forEach((elem, index) => {
      const instance = elem?.datagridInstance;
      if (!instance) return;
      const key = String(elem.id || ("datagrid:" + index));
      datagrids[key] = Array.isArray(instance.pendingRows)
        ? instance.pendingRows
        : [];
    });

    const flushables = {};
    form.querySelectorAll('[data-autoform-flushable="true"]').forEach((elem, index) => {
      if (typeof elem.getPendingSnapshot !== "function") return;
      const key = String(elem.id || elem.dataset.flushKey || ("flushable:" + index));
      flushables[key] = elem.getPendingSnapshot();
    });

    return JSON.stringify(this.#normalizeDirtyValue({
      fields,
      multiLists,
      datagrids,
      flushables,
    }));
  }

  /**
   * Ativa dirty tracking para o botão Salvar padrão de AutoForms editáveis.
   *
   * O baseline é capturado depois da renderização completa. Eventos nativos e
   * MutationObserver cobrem controles EP compostos sem acoplamento a cada
   * implementação. dirtyTracking false restaura o comportamento legado.
   *
   * @param {Object} options Opções runtime do AutoForm.
   * @returns {void}
   */
  #setupDirtyTracking(options = {}) {
    const enabled = options.dirtyTracking !== false
      && (options.mode === "edit" || options.mode === "new")
      && !Array.isArray(options.buttons);
    if (!enabled) return;

    const widget = options.__widget;
    const form = options.__form || widget?.querySelector?.("form");
    if (!form) return;

    const getSaveButton = () => (
      widget?.closest?.(".dialog")?.querySelector?.(".autoform-save-button")
      || widget?.querySelector?.(".autoform-save-button")
      || form.closest?.(".widget")?.querySelector?.(".autoform-save-button")
      || null
    );

    let baseline = this.#getDirtySnapshot(form);
    let scheduled = false;
    let explicitDirty = false;

    const apply = (forcedDirty = null) => {
      if (forcedDirty === true) explicitDirty = true;
      if (forcedDirty === false) explicitDirty = false;

      const dirty = explicitDirty
        || this.#getDirtySnapshot(form) !== baseline;
      options.__dirty = dirty;
      form.dataset.dirty = dirty ? "true" : "false";
      const saveButton = getSaveButton();
      if (saveButton) saveButton.disabled = !dirty;
      return dirty;
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        apply();
      });
    };

    const reset = () => {
      baseline = this.#getDirtySnapshot(form);
      explicitDirty = false;
      options.__dirtyBaseline = baseline;
      apply(false);
    };

    options.__dirtyBaseline = baseline;
    options.__resetDirty = reset;
    options.__isDirty = () => apply();
    options.__markDirty = () => apply(true);

    form.isDirty = options.__isDirty;
    form.resetDirty = reset;
    form.markDirty = options.__markDirty;

    form.addEventListener("input", schedule);
    form.addEventListener("change", schedule);
    form.addEventListener("autoform:dirty", () => apply(true));

    if (typeof MutationObserver === "function") {
      options.__dirtyObserver?.disconnect?.();
      options.__dirtyObserver = new MutationObserver(schedule);
      options.__dirtyObserver.observe(form, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["checked", "selected", "value"],
      });
    }

    apply(false);
    requestAnimationFrame(() => apply());
  }

  #hasMeaningfulFilterValue(form) {
    if (!form) return false;

    let snapshot;
    try {
      snapshot = JSON.parse(this.#getDirtySnapshot(form));
    } catch {
      return false;
    }

    const hasValue = (value) => {
      if (value === null || value === undefined || value === false) return false;
      if (typeof value === "string") return value.trim() !== "";
      if (typeof value === "number") return Number.isFinite(value);
      if (Array.isArray(value)) return value.some((item) => hasValue(item));
      if (typeof value === "object") {
        return Object.values(value).some((item) => hasValue(item));
      }
      return true;
    };

    return hasValue(snapshot?.fields || {});
  }

  #setupFilterActionState(options = {}) {
    if (options.mode !== "filter" || Array.isArray(options.buttons)) return;

    const widget = options.__widget;
    const form = options.__form || widget?.querySelector?.("form");
    if (!form) return;

    const getFilterButton = () => (
      widget?.closest?.(".dialog")?.querySelector?.(".autoform-filter-button")
      || widget?.querySelector?.(".autoform-filter-button")
      || null
    );

    let scheduled = false;
    const apply = () => {
      const button = getFilterButton();
      if (button) button.disabled = !this.#hasMeaningfulFilterValue(form);
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        apply();
      });
    };

    form.addEventListener("input", schedule);
    form.addEventListener("change", schedule);

    if (typeof MutationObserver === "function") {
      options.__filterStateObserver?.disconnect?.();
      options.__filterStateObserver = new MutationObserver(schedule);
      options.__filterStateObserver.observe(form, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["checked", "selected", "value"],
      });
    }

    apply();
    requestAnimationFrame(() => apply());
  }

  /**
   * Converte nome CSS kebab-case em camelCase.
   *
   * @param {string} [key=''] Chave.
   * @returns {string} Chave normalizada.
   */
  #normalizeStyleKey(key = '') {
    return String(key || '').trim().replace(/-([a-z])/g, (_, char) => char.toUpperCase());
  }

  /**
   * Normaliza valores de dimensões numéricas para pixels.
   *
   * @param {string} [key=''] Chave de estilo.
   * @param {*} value Valor.
   * @returns {*} Valor normalizado.
   */
  #normalizeStyleValue(key = '', value) {
    if (value === undefined || value === null) return undefined;
    const normalizedKey = this.#normalizeStyleKey(key);
    const numericLengthKeys = new Set([
      'width',
      'minWidth',
      'maxWidth',
      'height',
      'minHeight',
      'maxHeight',
    ]);

    if (typeof value === 'number' && numericLengthKeys.has(normalizedKey)) {
      return `${value}px`;
    }

    return value;
  }

  /**
   * Converte style string/objeto em objeto camelCase normalizado.
   *
   * @param {Object|string|false|null} style Estilo.
   * @returns {Object<string,*>} Estilo normalizado.
   */
  #normalizeStyle(style) {
    if (style === undefined || style === null || style === false) return {};

    if (typeof style === 'string') {
      return style
        .split(';')
        .map((item) => item.trim())
        .filter(Boolean)
        .reduce((acc, item) => {
          const separator = item.indexOf(':');
          if (separator <= 0) return acc;
          const key = this.#normalizeStyleKey(item.slice(0, separator));
          const value = item.slice(separator + 1).trim();
          const normalizedValue = this.#normalizeStyleValue(key, value);
          if (key && normalizedValue !== undefined && normalizedValue !== '') {
            acc[key] = normalizedValue;
          }
          return acc;
        }, {});
    }

    if (typeof style !== 'object' || Array.isArray(style)) return {};

    return Object.entries(style).reduce((acc, [rawKey, rawValue]) => {
      const key = this.#normalizeStyleKey(rawKey);
      const value = this.#normalizeStyleValue(key, rawValue);
      if (key && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  /**
   * Lê uma propriedade de style aceitando chave normalizada/original.
   *
   * @param {Object} [style={}] Estilo.
   * @param {string} [key=''] Chave.
   * @returns {*} Valor.
   */
  #styleValue(style = {}, key = '') {
    const normalizedKey = this.#normalizeStyleKey(key);
    return style?.[normalizedKey] ?? style?.[key];
  }

  /**
   * Resolve valor de campo em objeto/array de dados e mapas de fallback.
   *
   * @param {Object|Array} [data={}] Dados.
   * @param {string} [fieldName=''] Nome/id do campo.
   * @param {Array<Map|Object>} [fallbackMaps=[]] Fontes adicionais.
   * @returns {*} Valor encontrado.
   */
  #resolveFieldValueFromData(data = {}, fieldName = '', fallbackMaps = []) {
    const raw = String(fieldName || '').trim();
    if (!raw) return undefined;
    const short = raw.split('.').pop();

    /** @param {Object} source Fonte. @returns {*} Valor encontrado. */
    const readFromObject = (source) => {
      if (!source || typeof source !== 'object' || Array.isArray(source)) return undefined;
      if (Object.prototype.hasOwnProperty.call(source, raw)) {
        return source[raw];
      }
      if (short && Object.prototype.hasOwnProperty.call(source, short)) {
        return source[short];
      }
      const values = source.values && typeof source.values === 'object' && !Array.isArray(source.values)
        ? source.values
        : null;
      if (values) {
        if (Object.prototype.hasOwnProperty.call(values, raw)) return values[raw];
        if (short && Object.prototype.hasOwnProperty.call(values, short)) return values[short];
      }
      return undefined;
    };

    const direct = readFromObject(data);
    if (direct !== undefined) return direct;

    if (Array.isArray(data)) {
      for (const item of data) {
        const name = String(item?.name || '').trim();
        if (!name) continue;
        const itemShort = name.split('.').pop();
        if (name === raw || (short && itemShort === short)) {
          return item?.value;
        }
      }
    }

    for (const map of (Array.isArray(fallbackMaps) ? fallbackMaps : [])) {
      if (map instanceof Map) {
        if (map.has(raw)) return map.get(raw);
        if (short && map.has(short)) return map.get(short);
        continue;
      }
      const value = readFromObject(map);
      if (value !== undefined) return value;
    }

    return undefined;
  }

  /**
   * Carrega e cacheia `getConfigScreen` de um DataGrid aninhado.
   *
   * A chave usa o target normalizado com `service` canônico.
   *
   * @param {Object} [field={}] Configuração do DataGrid.
   * @returns {Promise<Object>} Configuração de tela.
   * @see normalizeServiceTarget
   */
  async #getDatagridConfig(field = {}) {
    const key = field?.key || 'main';
    const target = normalizeServiceTarget({
      ...field,
      method: 'getConfigScreen',
    });
    const cacheKey = [
      target.scope,
      target.product || '',
      target.service || '',
      target.domain || '',
      target.module || '',
      key,
    ].join('|');

    if (!this.configScreenCache.has(cacheKey)) {
      this.configScreenCache.set(
        cacheKey,
        await this.#post(field, 'getConfigScreen', { key }, { loader: false })
      );
    }

    return this.configScreenCache.get(cacheKey);
  }

  /**
   * Deriva relação pai/filho de DataGrid aninhado e condição correspondente.
   *
   * @param {Object} [field={}] Configuração do DataGrid.
   * @param {Object} [data={}] Registro pai.
   * @param {Map} [conditionsMap=new Map()] Condições do AutoForm.
   * @returns {Promise<Object|null>} Contexto de relação.
   */
  async #buildDatagridRelationContext(field = {}, data = {}, conditionsMap = new Map()) {
    const config = await this.#getDatagridConfig(field);
    const relation = field?.relation || config?.relation;
    const parentField = String(relation?.parent || '').trim();
    const childField = String(relation?.child || '').trim();
    if (!parentField || !childField) return null;

    const parentValue = this.#resolveFieldValueFromData(data, parentField, [conditionsMap]);
    if (parentValue === undefined || parentValue === null || String(parentValue).trim() === '') {
      return null;
    }

    return {
      parentField,
      childField,
      parentValue,
      conditions: [{
        key: childField,
        operator: '=',
        value: parentValue,
        raw: false,
        trusted: false,
      }]
    };
  }

  /**
   * Mescla condições do DataGrid substituindo condições para a mesma chave da
   * relação pai/filho.
   *
   * @param {Array<Object>} [baseConditions=[]] Condições originais.
   * @param {Array<Object>} [relationConditions=[]] Condições da relação.
   * @returns {Array<Object>} Condições mescladas.
   */
  #mergeRelationConditions(baseConditions = [], relationConditions = []) {
    const relationKeys = new Set(
      (Array.isArray(relationConditions) ? relationConditions : [])
        .map((cond) => String(cond?.key || '').trim())
        .filter(Boolean)
    );
    const relationShortKeys = new Set(
      [...relationKeys]
        .map((key) => key.split('.').pop())
        .filter(Boolean)
    );

    const base = (Array.isArray(baseConditions) ? baseConditions : [])
      .filter((cond) => {
        const key = String(cond?.key || '').trim();
        if (!key) return false;
        if (relationKeys.has(key)) return false;
        return !relationShortKeys.has(key.split('.').pop());
      });

    return [...base, ...relationConditions];
  }

  /**
   * Resolve o `<form>` associado a um evento de botão/API.
   *
   * @param {Event} e Evento.
   * @param {Object} [options={}] Opções com referências runtime.
   * @returns {HTMLFormElement|null} Form resolvido.
   */
  #resolveFormFromEvent(e, options = {}) {
    if (options?.__form?.isConnected) {
      return options.__form;
    }

    const target = e?.currentTarget || e?.target || null;

    const directForm = target?.closest?.("form");
    if (directForm) return directForm;

    const widgetForm = target?.closest?.(".widget")?.querySelector?.("form");
    if (widgetForm) return widgetForm;

    const dialogForm = target?.closest?.(".dialog")?.querySelector?.(":scope > .dialog-content form");
    if (dialogForm) return dialogForm;

    if (options?.id) {
      const byId = document.getElementById(options.id)?.querySelector?.("form");
      if (byId) return byId;
    }

    return document.querySelector(".dialog.show > .dialog-content form") || null;
  }

  /**
   * Limpa o form associado ao evento quando ele expõe `clearForm()`.
   *
   * @param {Event} e Evento.
   * @param {Object} [options={}] Opções.
   * @returns {void}
   */
  #clear(e, options = {}) {
    e?.preventDefault?.();
    const form = this.#resolveFormFromEvent(e, options);
    if (typeof form?.clearForm === "function") {
      form.clearForm();
    }
  }

  /**
   * Cancela/fecha o Dialog ou remove o widget inline.
   *
   * @param {Event} e Evento.
   * @param {Object} [options={}] Opções.
   * @returns {void}
   */
  #cancel(e, options = {}) {
    e?.preventDefault?.();

    const form = this.#resolveFormFromEvent(e, options);
    const dialog = e?.target?.closest?.(".dialog")?.instance
      || form?.closest?.(".dialog")?.instance
      || options?.__widget?.closest?.(".dialog")?.instance;

    if (dialog?.close) {
      dialog.close();
      return;
    }

    options?.__widget?.remove?.();
  }

  /**
   * Monta e renderiza o AutoForm completo.
   *
   * Fluxo resumido:
   * 1. normaliza id/mode;
   * 2. carrega configuração, grupos e dados quando não fornecidos localmente;
   * 3. resolve callbacks/dicionário e distribui dados pelos campos;
   * 4. prepara relações de DataGrid e modos read-only;
   * 5. cria Dialog/inline, Form, Group e botões;
   * 6. anexa referências runtime `__widget`/`__form` e abre o Dialog somente
   *    depois da renderização para evitar flicker.
   *
   * Callbacks serializados em string só são revividos quando a variável de
   * ambiente `ALLOW_UNSAFE_EVAL=1`; caso contrário permanecem texto e geram
   * warning de segurança.
   *
   * `filter.conditions` contém objetos com `key`, `operator`, `value`, `raw` e
   * `trusted`, conforme o contrato backend vigente.
   *
   * `saveHandler`, quando informado, recebe `{data, options, form, event}`. Se
   * não retornar um objeto, AutoForm executa a operação remota `save`.
   *
   * @param {Object} options Configuração do AutoForm.
   * @param {'edit'|'filter'|'view'|'new'|string} [options.mode='edit'] Modo operacional; valores inválidos viram `edit`.
   * @param {string} [options.key='main'] Chave do formulário.
   * @param {boolean} [options.isDialog=false] Renderiza dentro de Dialog.
   * @param {boolean} [options.singleton=false] Registro único do contexto: carrega os dados via `getDataForm` mesmo sem condições de chave.
   * @param {boolean} [options.autoOpen=true] Autoabre o Dialog após renderização completa.
   * @param {boolean} [options.kill=false] Política de destruição do Dialog.
   * @param {boolean} [options.isDraggable=false] Permite arrastar o Dialog.
   * @param {Object} [options.dialog] Overrides do Dialog.
   * @param {string} [options.title] Título explícito; tem precedência sobre configuração remota.
   * @param {Array<Object>} [options.groups] Grupos locais; evita consulta `getFormGroups`.
   * @param {Object<string,*>} [options.data] Registro local; evita consulta `getDataForm`.
   * @param {Object} [options.filter] Filtro de carga e contexto.
   * @param {string} [options.filter.key='main'] Chave da configuração/consulta.
   * @param {Array<Object>} [options.filter.conditions] Condições com `key`, `operator`, `value`, `raw` e `trusted`.
   * @param {string|string[]} [options.keys] Chave(s) primária(s) usadas para binding.
   * @param {string|string[]} [options.primaryKey] Alias alternativo de `keys`.
   * @param {number} [options.columns=1] Colunas do grupo raiz.
   * @param {number} [options.cols] Fallback de `columns`.
   * @param {Object|string} [options.groupStyle] Estilo do Group interno.
   * @param {Object|string} [options.contentStyle] Alias/fallback de `groupStyle`.
   * @param {Array<Object>} [options.buttons] Botões customizados.
   * @param {boolean} [options.dirtyTracking=true] Desabilita o Salvar padrão até haver alteração em modos edit/new.
   * @param {'array'|'object'} [options.returnMode='array'] Formato retornado por `getDataFields()`.
   * @param {Function} [options.callback] Callback de retorno/filtro ou pós-save.
   * @param {Function} [options.saveHandler] Save customizado; recebe `{data, options, form, event}`.
   * @param {boolean} [options.keepOpenOnSuccess=false] Mantém Dialog aberto após save bem-sucedido.
   * @param {boolean} [options.successToast=true] Exibe Toast de sucesso; erros sempre permanecem visíveis.
   * @param {string} [options.dictionary] Namespace de dicionário usado para resolver erros backend.
   * @param {string} [options.enctype] Atributo do `<form>`.
   * @param {string} [options.method] Atributo HTML do `<form>`; não define o método de serviço.
   * @param {string} [options.target] Atributo do `<form>`.
   * @returns {Promise<void>} Renderização concluída.
   * @see module:components/create/element
   * @see normalizeServiceTarget
   */
  async autoform(options) {
    const openLoader = new Loader();
    openLoader.start();
    try {
    const renderSequence = ++autoformSeq;
    const widgetIdent = `autoform_widget_${renderSequence}`;
    const formIdent = `autoform_form_${renderSequence}`;
    const buttonbarIdent = `autoform_buttonbar_${renderSequence}`;
    const targetParent = options.parent;
    const detachedMount = !options.isDialog && targetParent
      ? document.createElement("div")
      : null;
    const renderOptions = detachedMount
      ? { ...options, parent: detachedMount }
      : options;

    if (!renderOptions.id || String(renderOptions.id).trim() === "") {
      const base = String(renderOptions.module || "autoform")
        .trim()
        .replace(/[^a-zA-Z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "") || "autoform";
      renderOptions.id = `${base}_dialog_${renderSequence}`;
    }

    const modes = ["edit", "filter", "view", "new"];
    if (renderOptions.mode && modes.includes(renderOptions.mode.toLowerCase())) {
      renderOptions.mode = renderOptions.mode.toLowerCase();
    } else {
      renderOptions.mode = "edit";
    }

    let classname;

    if (renderOptions.isDialog) {
      classname = renderOptions.class;
    } else {
      classname = renderOptions.class !== undefined ? renderOptions.class + " widget" : "widget";
    }

    const formConfigKey = renderOptions.key || "main";
    let configForm = null;
    const explicitFormOptions = {
      title: renderOptions.title,
      description: renderOptions.description,
      iconDescription: renderOptions.iconDescription,
      groups: renderOptions.groups,
      style: renderOptions.style,
      groupStyle: renderOptions.groupStyle,
      contentStyle: renderOptions.contentStyle,
    };
    const needsConfigForm = !renderOptions.groups
      || renderOptions.title === undefined
      || renderOptions.title === null
      || String(renderOptions.title).trim() === "";
    const configFormPromise = needsConfigForm
      ? this.#post(
        renderOptions,
        "getConfigForm",
        { key: formConfigKey },
        { loader: false }
      )
      : null;

    let groupsPromise = null;
    if (!renderOptions.groups) {
      groupsPromise = this.#post(renderOptions, "getFormGroups", {
        key: formConfigKey,
      }, { loader: false });
    }

    const filter = renderOptions.filter || {};
    filter.key = filter?.key || "main";
    const dataPromise = (
      !(renderOptions.data && typeof renderOptions.data === "object" && !Array.isArray(renderOptions.data))
      && renderOptions.mode !== "new"
      // `singleton`: formulário de registro ÚNICO do contexto (ex.: configuração do tenant), carregado sem condição de chave; o backend escopa o registro.
      && (renderOptions.singleton === true || (Array.isArray(filter.conditions) && filter.conditions.length > 0))
    )
      ? this.#post(
        renderOptions,
        "getDataForm",
        filter,
        { loader: false }
      )
      : null;

    if (!renderOptions.groups) {
      configForm = await configFormPromise;
      Object.assign(renderOptions, configForm);
      for (const [key, value] of Object.entries(explicitFormOptions)) {
        if (value !== undefined) {
          renderOptions[key] = value;
        }
      }
    }

    if (renderOptions.title === undefined || renderOptions.title === null || String(renderOptions.title).trim() === "") {
      if (!configForm) {
        configForm = await configFormPromise;
      }
      const fallbackTitle = configForm?.title;
      if (fallbackTitle !== undefined && fallbackTitle !== null && String(fallbackTitle).trim() !== "") {
        renderOptions.title = fallbackTitle;
      }
    }

    let groups =
      renderOptions.groups ||
      (await groupsPromise);
    // Preserva callbacks já-função e reconstrói apenas callbacks serializados em string.
    /**
     * Revive callbacks serializados recursivamente quando `ALLOW_UNSAFE_EVAL=1`.
     *
     * @param {*} value Nó de configuração.
     * @param {string} [keyPath='root'] Caminho para diagnóstico.
     * @returns {*} Estrutura resolvida.
     */
    const reviveCallbacksDeep = (value, keyPath = "root") => {
      if (Array.isArray(value)) {
        return value.map((item, idx) => reviveCallbacksDeep(item, `${keyPath}[${idx}]`));
      }

      if (value && typeof value === "object") {
        const out = {};
        for (const [key, nested] of Object.entries(value)) {
          out[key] = reviveCallbacksDeep(nested, `${keyPath}.${key}`);
        }
        return out;
      }

      if (typeof value === "string" && looksLikeFunctionSource(value)) {
        if (!allowUnsafeEval) {
          console.warn(`[security] Callback em string bloqueado em autoform (${keyPath}).`);
          return value;
        }
        try {
          const callable = Function(`"use strict"; return (${value});`)();
          return typeof callable === "function" ? callable : value;
        } catch (err) {
          console.warn(
            `Callback serializado inválido no autoform (${keyPath}); ele foi mantido como texto.`,
            String(value).trim().slice(0, 160),
            err
          );
          return value;
        }
      }

      return value;
    };

    groups = reviveCallbacksDeep(groups);
    groups = this.#resolveDicDeep(groups);

    let data = {};
    if (renderOptions.data && typeof renderOptions.data === "object" && !Array.isArray(renderOptions.data)) {
      data = { ...renderOptions.data };
    } else if (dataPromise) {
      const dataResult = await dataPromise;
      // Registro único do contexto sem confirmação do backend (ex.: contrato indisponível): erro explícito; o formulário NÃO abre com valores padrão.
      if (renderOptions.singleton === true && dataResult?.status === "error") {
        msgbox({ type: resolveBackendErrorType(dataResult), message: this.#resolveBackendErrorMessage(dataResult, renderOptions) || dic?.message?.errorOperation || "Não foi possível concluir a operação." });
        return null;
      }
      if (dataResult.status === "success" && dataResult.rows > 0) {
        data = dataResult.data[0] || {};
      }
    }

    const conditionsMap = new Map();
    if (Array.isArray(filter.conditions)) {
      for (const cond of filter.conditions) {
        const fullKey = String(cond?.key || '').trim();
        const shortKey = fullKey.split('.').pop();
        if (!fullKey) continue;
        if (cond?.value === undefined || cond?.value === null) continue;
        conditionsMap.set(fullKey, cond.value);
        if (shortKey) conditionsMap.set(shortKey, cond.value);
      }
    }
    const primaryKeyConfig = renderOptions.keys || renderOptions.primaryKey;
    const primaryKeyFields = new Set(
      (Array.isArray(primaryKeyConfig)
        ? primaryKeyConfig
        : [primaryKeyConfig])
        .map((key) => String(key || '').trim().split('.').pop())
        .filter(Boolean)
    );

    renderOptions.title = this.#resolveDicRef(renderOptions.title);

    const formStyle = this.#normalizeStyle(
      renderOptions.style !== undefined ? renderOptions.style : options.style
    );
    const widgetStyle = { ...formStyle };
    const groupStyle = this.#normalizeStyle(
      renderOptions.groupStyle ?? renderOptions.contentStyle ?? {}
    );
    const elem = [];

    /**
     * Achata recursivamente campos declarativos dentro de content/groups.
     *
     * @param {Array<Object>} [items=[]] Itens.
     * @param {Array<Object>} [output=[]] Acumulador.
     * @returns {Array<Object>} Campos encontrados.
     */
    const collectFields = (items = [], output = []) => {
      if (!Array.isArray(items)) return output;
      for (const item of items) {
        if (!item || typeof item !== "object") continue;
        if (item.element) output.push(item);
        if (Array.isArray(item.content)) collectFields(item.content, output);
        if (Array.isArray(item.groups)) {
          for (const group of item.groups) {
            collectFields(Array.isArray(group?.content) ? group.content : [], output);
          }
        }
      }
      return output;
    };

    const allFields = collectFields(groups.flatMap((group) =>
      Array.isArray(group?.content) ? group.content : []
    ));

    const datagridFields = [];
    const inheritedServiceContext = {
      ...(renderOptions.context && typeof renderOptions.context === 'object'
        ? renderOptions.context
        : {}),
      ...(renderOptions.scope ? { scope: renderOptions.scope } : {}),
      ...(renderOptions.product ? { product: renderOptions.product } : {}),
      ...(renderOptions.service ? { service: renderOptions.service } : {}),
      ...(renderOptions.category ? { category: renderOptions.category } : {}),
      ...(renderOptions.domain ? { domain: renderOptions.domain } : {}),
      ...(renderOptions.module ? { module: renderOptions.module } : {}),
    };
    for (const field of allFields) {
        if (field.loader === undefined) {
          field.loader = false;
        }
        const candidates = [
          field.name,
          ...(Array.isArray(field.id) ? field.id : [field.id]),
          field.alias,
        ]
          .map((key) => String(key || '').trim())
          .filter(Boolean);

        const dotted = candidates
          .filter((key) => typeof key === "string" && key.includes("."))
          .map((key) => key.split(".").pop());

        const keys = [...new Set([...candidates, ...dotted].filter(Boolean))];
        const dataKey = keys.find((key) =>
          key && Object.prototype.hasOwnProperty.call(data, key)
        );
        const conditionKey = keys.find((key) =>
          key && primaryKeyFields.has(String(key).split('.').pop()) && conditionsMap.has(key)
        );

        switch (field.element) {
          case "boardLayoutEditor": {
            field.autoformMode = renderOptions.mode;
            const contextField = field.contextField || 'id';
            field.cardTypeId = data[contextField] ?? conditionsMap.get(contextField) ?? null;
            break;
          }
          case "datagrid": {
            field.loader = false;
            field.context = resolveNestedServiceContext(inheritedServiceContext, field);
            datagridFields.push(field);
            break;
          }
          case "address": {
            const sourceKey = dataKey;
            const values =
              sourceKey && data[sourceKey] !== undefined
                ? data[sourceKey]
                : data;
            field.values = values || null;
            break;
          }
          case "geography": {
            const sourceKey = dataKey;
            const sourceValues =
              sourceKey
              && data[sourceKey]
              && typeof data[sourceKey] === "object"
              && !Array.isArray(data[sourceKey])
                ? data[sourceKey]
                : data;
            field.values = {
              ...(field.values && typeof field.values === "object" && !Array.isArray(field.values)
                ? field.values
                : {}),
              ...(sourceValues && typeof sourceValues === "object" && !Array.isArray(sourceValues)
                ? sourceValues
                : {}),
            };
            for (const [key, value] of conditionsMap.entries()) {
              if (!key || field.values[key] !== undefined) continue;
              field.values[key] = value;
            }
            break;
          }
          case "geodoc": {
            const sourceKey = dataKey;
            const sourceValues =
              sourceKey
              && data[sourceKey]
              && typeof data[sourceKey] === "object"
              && !Array.isArray(data[sourceKey])
                ? data[sourceKey]
                : data;
            field.values = {
              ...(field.values && typeof field.values === "object" && !Array.isArray(field.values)
                ? field.values
                : {}),
              ...(sourceValues && typeof sourceValues === "object" && !Array.isArray(sourceValues)
                ? sourceValues
                : {}),
            };
            for (const [key, value] of conditionsMap.entries()) {
              if (!key || field.values[key] !== undefined) continue;
              field.values[key] = value;
            }
            break;
          }
          case "checkbox":
          case "radio": {
            const current = dataKey ? data[dataKey] : undefined;
            let isChecked = false;

            if (Array.isArray(current)) {
              const target = field.value ?? field.text;
              isChecked = current.includes(target);
            } else if (typeof current === "boolean") {
              // Evita tratar value: false no config como "valor do input" e marcar checked quando current === false.
              isChecked = current;
            } else if ((current === undefined || current === null) && field.element === "checkbox" && field.defaultValue !== undefined) {
              const v = field.defaultValue;
              isChecked =
                v === true ||
                v === 1 ||
                v === "1" ||
                v === "true";
            } else if (!dataKey && field.element === "checkbox") {
              // Form novo (campo não veio no getDataForm): value/defaultValue true liga o switch por padrão.
              const v = field.value !== undefined ? field.value : field.defaultValue;
              isChecked =
                v === true ||
                v === 1 ||
                v === "1" ||
                v === "true";
            } else {
              const expected = field.value ?? true;
              isChecked =
                current === true ||
                current === 1 ||
                current === "1" ||
                current === "true" ||
                (expected !== false && current === expected) ||
                (field.text != null && current === field.text);
            }

            field.checked = Boolean(isChecked);
            break;
          }
          case "inputGroup": {
            if (Array.isArray(field.id)) {
              const boundValues = field.id.map((fieldId) => {
                const rawId = String(fieldId || '').trim();
                const shortId = rawId.split('.').pop();

                if (rawId && data[rawId] !== undefined) return data[rawId];
                if (shortId && data[shortId] !== undefined) return data[shortId];
                if (rawId && conditionsMap.has(rawId)) return conditionsMap.get(rawId);
                if (shortId && conditionsMap.has(shortId)) return conditionsMap.get(shortId);
                return undefined;
              });
              if (boundValues.some((value) => value !== undefined)) {
                field.value = boundValues;
              } else {
                delete field.value;
              }
              break;
            }

            const current = data[field.id] ?? (dataKey ? data[dataKey] : null);
            if (current !== undefined && current !== null) {
              field.value = Array.isArray(current) ? [...current] : current;
            }
            break;
          }
          case "upload": {
            field.values = data || {};
            if (renderOptions.mode !== "new" && conditionKey) {
              field.value = conditionsMap.get(conditionKey);
            } else if (dataKey && data[dataKey] !== undefined) {
              field.value = data[dataKey];
            } else if (renderOptions.mode === "new") {
              const v = conditionsMap.get(field.name) ?? conditionsMap.get(field.id);
              if (v !== undefined) {
                field.value = v;
              }
            } else if (field.value === undefined) {
              field.value = null;
            }
            break;
          }
          case "multiList": {
            field.values = { ...(data || {}) };
            for (const key of primaryKeyFields) {
              if (!key || field.values[key] !== undefined || !conditionsMap.has(key)) continue;
              field.values[key] = conditionsMap.get(key);
            }
            break;
          }
          case "combo":
          case "tagbox": {
            field.values = {
              ...(field.values && typeof field.values === "object" && !Array.isArray(field.values)
                ? field.values
                : {}),
              ...(data || {}),
            };
            for (const [key, value] of conditionsMap.entries()) {
              if (!key || field.values[key] !== undefined) continue;
              field.values[key] = value;
            }

            const current = dataKey ? data[dataKey] : undefined;
            const hasConcreteValue = current !== undefined && current !== null && current !== "";

            if (renderOptions.mode !== "new" && conditionKey) {
              field.value = conditionsMap.get(conditionKey);
            } else if (dataKey && hasConcreteValue) {
              field.value = current;
            } else if (renderOptions.mode === "new") {
              const v = conditionsMap.get(field.name) ?? conditionsMap.get(field.id);
              if (v !== undefined) {
                field.value = v;
              }
            } else if (field.defaultValue !== undefined) {
              field.value = field.defaultValue;
            } else if (field.value === undefined) {
              field.value = null;
            }
            break;
          }
          case "json": {
            if (renderOptions.mode !== "new" && conditionKey) {
              field.value = conditionsMap.get(conditionKey);
            } else if (dataKey && data[dataKey] !== undefined && data[dataKey] !== null) {
              field.value = data[dataKey];
            } else if (renderOptions.mode === "new") {
              const v = conditionsMap.get(field.name) ?? conditionsMap.get(field.id);
              if (v !== undefined && v !== null) {
                field.value = v;
              } else {
                delete field.value;
              }
            } else {
              delete field.value;
            }
            break;
          }
          default: {
            if (Array.isArray(field.id)) {
              // Composite com par de ids (ex.: `period`). Um `dataKey` só
              // resolveria o primeiro id do par e duplicaria seu valor nos
              // dois lados; aqui cada id do par busca sua própria propriedade.
              const pairedValue = field.id.map((idPart) => {
                const rawId = String(idPart || '').trim();
                const shortId = rawId.split('.').pop();
                if (rawId && data[rawId] !== undefined) return data[rawId];
                if (shortId && data[shortId] !== undefined) return data[shortId];
                return null;
              });
              field.value = pairedValue.some((value) => value !== null)
                ? pairedValue
                : (field.value === undefined ? null : field.value);
              break;
            }
            if (renderOptions.mode !== "new" && conditionKey) {
              field.value = conditionsMap.get(conditionKey);
            } else if (dataKey && data[dataKey] !== undefined) {
              field.value = data[dataKey];
            } else if (renderOptions.mode === "new") {
              const v = conditionsMap.get(field.name) ?? conditionsMap.get(field.id);
              if (v !== undefined) {
                field.value = v;
              }
            } else if (field.value === undefined) {
              field.value = null;
            }

            if (
              field.element === "spinner"
              && (field.value === null || field.value === "")
              && field.defaultValue !== undefined
            ) {
              delete field.value;
            }
            break;
        }

        if (field.element === "iconPicker") {
          const rawColorField = String(field.colorField || "").trim();
          const colorCandidates = [
            rawColorField,
            rawColorField.split(".").pop(),
          ].filter(Boolean);
          const colorKey = colorCandidates.find((key) => (
            Object.prototype.hasOwnProperty.call(data, key)
          ));
          const conditionColorKey = colorCandidates.find((key) => conditionsMap.has(key));
          const linkedColor = colorKey
            ? data[colorKey]
            : (conditionColorKey ? conditionsMap.get(conditionColorKey) : undefined);

          if (linkedColor !== undefined && linkedColor !== null && String(linkedColor).trim() !== "") {
            field.color = linkedColor;
          }
        }
      }

    if (datagridFields.length > 0) {
      const relationContexts = await Promise.all(
        datagridFields.map((field) => this.#buildDatagridRelationContext(field, data, conditionsMap))
      );
      relationContexts.forEach((relationContext, index) => {
        const field = datagridFields[index];
        if (relationContext && Array.isArray(relationContext.conditions)) {
          field.relationParentValue = relationContext.parentValue;
          field.conditions = this.#mergeRelationConditions(field.conditions, relationContext.conditions);
        }
      });
    }

        if (renderOptions.mode === "view") {
          field.readOnly = true;
        }

        if (renderOptions.mode === "edit" && field?.lockOnEdit === true) {
          field.readOnly = true;
          field.disabled = true;
        }
    }

    // Um único formulário para new/edit/view: `disabledNew` (desabilitado ao criar) e `readOnlyEdit` (somente leitura ao
    // editar) mantêm o campo visível; `view` torna todos os campos somente leitura.
    applyAutoformFieldModes(allFields, {
      mode: renderOptions.mode,
      disabledNew: renderOptions.disabledNew,
      readOnlyEdit: renderOptions.readOnlyEdit,
    });

    const editorFields = allFields.filter((field) => field?.element === "editor");
    const hasRichEditor = editorFields.length > 0;
    const editorBaseMinWidth = editorFields.reduce((max, field) => {
      const raw = Number(field?.editorMinWidth ?? field?.dialogMinWidth ?? field?.minWidth ?? 450);
      return Number.isFinite(raw) && raw > max ? raw : max;
    }, 0);
    const dialogEditorMinWidth = hasRichEditor
      ? Math.max(450, editorBaseMinWidth)
      : null;
    const dialogEditorWidth = hasRichEditor
      ? Math.max(620, editorBaseMinWidth + 72)
      : null;

    const pageKey = session.get('slug') || session.get('page');
    const shouldAutoOpenDialog = renderOptions.isDialog === true
      ? (renderOptions.autoOpen ?? true)
      : false;

    if (renderOptions.isDialog === true) {
      const existingDialog = renderOptions.dialog && typeof renderOptions.dialog === 'object'
        ? renderOptions.dialog
        : {};
      const formWidth = this.#styleValue(formStyle, 'width');
      const formMinWidth = this.#styleValue(formStyle, 'minWidth');
      const formMaxWidth = this.#styleValue(formStyle, 'maxWidth');
      let buttons;
      if (renderOptions.buttons) {
        buttons = renderOptions.buttons;
      } else if (renderOptions.mode === "edit" || renderOptions.mode === "new") {
        buttons = [
          {
            text: dic.labels.cancel,
            color: "red",
            icon: { type: "solid", name: "ban" },
            close: false,
            click: (e) => {
              this.#cancel(e, renderOptions);
            },
          },
          {
            text: dic.labels.save,
            color: "green",
            icon: { type: "solid", name: "floppy-disk" },
            class: "autoform-save-button",
            disabled: (
              (renderOptions.mode === "edit" || renderOptions.mode === "new")
              && renderOptions.dirtyTracking !== false
            ),
            data: { button: "enter" },
            close: false,
            click: (e) => {
              this.#save(e, renderOptions);
            },
          },
        ];
      } else if (renderOptions.mode === "filter") {
        buttons = [
          {
            text: dic.labels.cancel,
            color: "red",
            icon: { type: "solid", name: "ban" },
            close: false,
            click: (e) => {
              this.#cancel(e, renderOptions);
            },
          },
          {
            text: dic.labels.filter,
            icon: { type: "solid", name: "filter" },
            class: "autoform-filter-button",
            disabled: true,
            data: { button: "enter" },
            close: true,
            click: (e) => {
              this.getDataFields(e, renderOptions);
              this.#clear(e, renderOptions);
            },
          },
        ];
      } else {
        buttons = null;
      }
      renderOptions.dialog = {
        autoform: true,
        class: "autoform-dialog",
        modal: true,
        effect: "zoom",
        title: this.#resolveDicRef(renderOptions.title) || dic[pageKey]?.form?.title || dic[pageKey]?.title || dic.labels?.form,
        // Open manually after all form elements are rendered to avoid first-frame flicker.
        autoOpen: false,
        kill: renderOptions.kill ?? false,
        draggable: renderOptions.isDraggable ?? false,
        buttons: buttons,
        width: existingDialog.width ?? formWidth ?? dialogEditorWidth ?? undefined,
        minWidth: existingDialog.minWidth ?? formMinWidth ?? dialogEditorMinWidth ?? undefined,
        maxWidth: existingDialog.maxWidth ?? formMaxWidth ?? undefined,
        ...existingDialog,
      };

      // Em formulários modais, width/minWidth/maxWidth pertencem ao diálogo.
      // Reaplicá-los no widget interno soma padding e scrollbar à mesma medida
      // e corta a última coluna. O conteúdo deve ocupar a área útil do diálogo.
      delete widgetStyle.width;
      delete widgetStyle.minWidth;
      delete widgetStyle.maxWidth;
    } else {
      renderOptions.isDialog = false;
      classname += " widget-content";
    }

    elem.push({
      ident: widgetIdent,
      element: "div",
      id: renderOptions.id,
      parent: renderOptions.parent,
      class: classname,
      style: widgetStyle,
      dialog: renderOptions.dialog,
    });

    const formDescription = this.#resolveDicRef(
      renderOptions.description
      || dic[renderOptions.module]?.form?.description
      || dic[pageKey]?.form?.description
      || ''
    );
    if (String(formDescription || '').trim() !== '') {
      const descriptionIdent = `${widgetIdent}-description`;
      elem.push({
        ident: descriptionIdent,
        element: 'div',
        parent: `:${widgetIdent}`,
        class: 'autoform-description',
      });
      if (renderOptions.iconDescription) {
        elem.push({
          element: 'span',
          parent: `:${descriptionIdent}`,
          class: 'autoform-description-icon',
          icon: renderOptions.iconDescription,
        });
      }
      elem.push({
        element: 'div',
        parent: `:${descriptionIdent}`,
        class: 'autoform-description-content',
        html: formDescription,
      });
    }

    if (!renderOptions.isDialog) {
      elem.push({
        element: "h1",
        parent: `:${widgetIdent}`,
        text: renderOptions.title || dic.labels.form,
      });
    }

    elem.push({
      ident: formIdent,
      element: "form",
      parent: `:${widgetIdent}`,
      enctype: renderOptions.enctype,
      method: renderOptions.method,
      target: renderOptions.target,
    });
    const resolvedFormColumns = Number.isFinite(Number(renderOptions.columns))
      ? Number(renderOptions.columns)
      : Number.isFinite(Number(renderOptions.cols))
        ? Number(renderOptions.cols)
        : 1;

    elem.push({
      element: "group",
      parent: `:${formIdent}`,
      type: renderOptions.type,
      cols: resolvedFormColumns,
      style: groupStyle,
      groups: groups,
    });

    if (!renderOptions.isDialog) {
      elem.push({
        ident: buttonbarIdent,
        element: "div",
        parent: `:${widgetIdent}`,
        class: "autoform-buttons",
      });

      if (renderOptions.buttons && Array.isArray(renderOptions.buttons)) {
        for (const i in renderOptions.buttons) {
          elem.push({
            element: "button",
            parent: `:${buttonbarIdent}`,
            text: renderOptions.buttons[i].text,
            color: renderOptions.buttons[i].color,
            icon: renderOptions.buttons[i].icon,
            click: renderOptions.buttons[i].click
          });
        }
      } else {
        elem.push({
          element: "button",
          parent: `:${buttonbarIdent}`,
          text: dic.labels.cancel,
          color: "red",
          icon: { type: "solid", name: "ban" },
          close: false,
          click: (e) => {
            this.#cancel(e, renderOptions);
          },
        });
        elem.push({
          element: "button",
          parent: `:${buttonbarIdent}`,
          text: dic.labels.save,
          color: "green",
          icon: { type: "solid", name: "floppy-disk" },
          class: "autoform-save-button",
          disabled: (
              (renderOptions.mode === "edit" || renderOptions.mode === "new")
              && renderOptions.dirtyTracking !== false
            ),
          click: (e) => {
            this.#save(e, renderOptions);
          },
        });
      }
    }

    await create(elem);

    const widgetId = renderOptions.id;
    const resolvedRoot = document.getElementById(widgetId)
      || document.querySelector(`#${widgetId}`)
      || document.querySelector(".dialog.show .widget:last-of-type")
      || document.querySelector(".widget:last-of-type");
    const resolvedForm = resolvedRoot?.querySelector?.("form") || null;

    renderOptions.__widget = resolvedRoot || null;
    renderOptions.__form = resolvedForm;

    if (renderOptions.isDialog === true && shouldAutoOpenDialog) {
      const resolvedParent =
        renderOptions.parent instanceof Element
          ? renderOptions.parent
          : typeof renderOptions.parent === "string"
            ? document.querySelector(renderOptions.parent)
            : null;

      const widget = document.getElementById(widgetId)
        || resolvedParent?.querySelector(".widget:last-of-type")
        || document.querySelector(".widget:last-of-type");

      if (widget?.dialog) {
        requestAnimationFrame(() => {
          widget.dialog("open");
        });
      }
    }

    if (detachedMount) {
      const resolvedParent =
        targetParent instanceof Element
          ? targetParent
          : typeof targetParent === "string"
            ? document.querySelector(targetParent)
            : null;

      if (!resolvedParent) {
        console.error("Elemento pai não encontrado.");
        return;
      }

      while (detachedMount.firstChild) {
        resolvedParent.append(detachedMount.firstChild);
      }

      renderOptions.__widget = document.getElementById(widgetId) || renderOptions.__widget;
      renderOptions.__form = renderOptions.__widget?.querySelector?.("form") || renderOptions.__form;
    }

    this.#setupDirtyTracking(renderOptions);
    this.#setupFilterActionState(renderOptions);
    } finally {
      openLoader.end();
    }
  }

  /**
   * Serializa os campos do AutoForm sem persistir.
   *
   * Trata OTP, checkbox/radio, date, JSON, números, máscaras e Phone. Campos
   * auxiliares `-acronym` e DDI do Phone não são retornados isoladamente.
   *
   * Se `callback` existir, o resultado é entregue ao callback e, em Dialog, ele
   * é fechado antes da chamada. Sem callback, retorna o valor diretamente.
   *
   * @param {Event} e Evento/contexto do formulário.
   * @param {Object} options Opções do AutoForm; veja {@link AutoForm#autoform}.
   * @returns {Promise<Array<{name:string,value:*}>|Object<string,*>>|Promise<void>} Dados serializados.
   */
  async getDataFields(e, options) {
    const returnMode = options?.returnMode || 'array';
    const form = this.#resolveFormFromEvent(e, options);
    const data = [];
    const processed = [];
    const errors = [];
    let mandatory = [];
    let value, phoneddi;

    if (form) {
      const elements = this.#getFormControls(form);

      elements.forEach((el) => {
        const isPhoneAcronym = el.name?.endsWith("-acronym")
          && el.closest(".input-wrapper.phone");

        if (
          !el.name ||
          el.disabled ||
          processed.includes(el.name) ||
          el.dataset.id === "ddi" ||
          isPhoneAcronym
        )
          return;

        const choices = ["radio", "checkbox"];

        if (el.dataset.type === 'otp-hidden') {
          const scope = el.closest('.fieldbox') || form;
          value = Array.from(scope.querySelectorAll('input[data-type="otp"]'))
            .map((input) => (input.value || '').trim())
            .join('');
        } else if (choices.includes(el.type)) {
          const selected = elements.filter((candidate) => (
            candidate.name === el.name && candidate.checked
          ));
          const sameNameCheckboxes = el.type === "checkbox"
            ? elements.filter((candidate) => (
              candidate.type === "checkbox" && candidate.name === el.name
            ))
            : [];
          const isCheckboxGroup = el.type === "checkbox" && sameNameCheckboxes.length > 1;

          if (selected.length === 1) {
            if (isCheckboxGroup) {
              value = [selected[0].value];
            } else {
              value = selected[0].value == "on" ? true : selected[0].value;
            }
          } else if (selected.length > 1) {
            value = Array.from(selected).map((input) => input.value);
          } else if (el.type === "radio") {
            const label = el
              .closest(".fieldbox")
              ?.querySelector("label")?.innerText;
            if (label) mandatory.push(label);
          } else {
            value = isCheckboxGroup ? [] : false;
          }
        } else if (el.dataset.type === "date") {
          value = format.dateDB(el.value);
        } else if (el.dataset.type === "json") {
          try {
            value = JSON.parse(el.value);
          } catch {
            value = el.value;
          }
        } else if (
          el.dataset.type === "integer" ||
          el.dataset.type === "number" ||
          el.dataset.type === "currency"
        ) {
          value = format.numberDB(el.value);
        } else if (el.dataset.masked === "true") {
          value = el.getUnmasked().value || '';
          if (el.dataset.type === "phone") {
            const isNational = el.dataset.national === "true";
            const scope = el.closest('.fieldbox') || form;
            const ddiInput = scope?.querySelector(`[name="${el.name}-ddi"]`) || form.querySelector(`[name="${el.name}-ddi"]`);
            const ddiRaw = ddiInput?.value || el.dataset.ddi || '';
            const ddiDigits = ddiRaw.replace(/\D/g, '');
            let digits = value.replace(/\D/g, '');
            if (!isNational && ddiDigits && digits.startsWith(ddiDigits)) {
              digits = digits.slice(ddiDigits.length);
            }
            value = digits.length > 0 ? `${ddiDigits}${digits}` : '';
          }
        } else {
          value = el.value;
        }

        if (el.required === true) {
          if (!value || value.length === 0) {
            mandatory.push(
              el.closest(".fieldbox").querySelector("label").innerText
            );
            el.data("valid", false);
            el.closest(".fieldbox").classList.add("invalid");
            el.closest(".fieldbox").classList.remove("valid");
            errors.push(el.name);
          } else {
            mandatory = mandatory.filter((item) => item !== el.name);
            el.data("valid", true);
            el.closest(".fieldbox").classList.remove("invalid");
            processed.push(el.name);
            data.push({ name: el.name, value });
          }
        } else {
          processed.push(el.name);
          data.push({ name: el.name, value });
        }
      });
    }

    const result =
      returnMode === "object"
        ? data.reduce((acc, { name, value }) => {
          if (!name) return acc;
          if (acc[name] !== undefined) {
            // preserva múltiplas seleções no mesmo nome
            if (Array.isArray(acc[name])) acc[name].push(value);
            else acc[name] = [acc[name], value];
          } else {
            acc[name] = value;
          }
          return acc;
        }, {})
        : data;

    if (options?.callback && typeof options?.callback === "function") {
      if (options.isDialog) {
        const dialog = e.target.closest(".dialog").instance;
        dialog.close();
      }

      options.callback(result);
    } else {
      return result;
    }
  }

  /**
   * Valida, serializa e persiste o AutoForm.
   *
   * Valida campos nativos e MultiList obrigatório, usa `saveHandler` quando
   * fornecido e faz fallback para operação `save`. Após sucesso, descarrega
   * DataGrids aninhados pendentes, mostra Toast, fecha Dialog conforme
   * `keepOpenOnSuccess` e executa `callback` com o primeiro registro retornado.
   *
   * @param {Event} e Evento de save.
   * @param {Object} options Opções do AutoForm; veja {@link AutoForm#autoform}.
   * @returns {Promise<Object|false>} Resultado backend/custom ou `false` em validação/contexto inválido.
   */
  async #save(e, options) {
    const form = this.#resolveFormFromEvent(e, options);
    const data = [];
    const processed = [];
    const errors = [];
    let mandatory = [];
    let value, phoneddi;

    if (!form) {
      console.warn("AutoForm: form não encontrado no contexto do botão de salvar.");
      msgbox({ type: "warning", message: dic?.message?.errorOperation || "Formulário inválido." });
      return false;
    }

    {
      const elements = this.#getFormControls(form);

      elements.forEach((el) => {
        const isPhoneAcronym = el.name?.endsWith("-acronym")
          && el.closest(".input-wrapper.phone");

        if (
          !el.name ||
          el.disabled ||
          processed.includes(el.name) ||
          el.dataset.id === "ddi" ||
          isPhoneAcronym
        )
          return;

        const choices = ["radio", "checkbox"];

        if (el.dataset.type === 'otp-hidden') {
          const scope = el.closest('.fieldbox') || form;
          value = Array.from(scope.querySelectorAll('input[data-type="otp"]'))
            .map((input) => (input.value || '').trim())
            .join('');
        } else if (choices.includes(el.type)) {
          const selected = elements.filter((candidate) => (
            candidate.name === el.name && candidate.checked
          ));
          const sameNameCheckboxes = el.type === "checkbox"
            ? elements.filter((candidate) => (
              candidate.type === "checkbox" && candidate.name === el.name
            ))
            : [];
          const isCheckboxGroup = el.type === "checkbox" && sameNameCheckboxes.length > 1;

          if (selected.length === 1) {
            if (isCheckboxGroup) {
              value = [selected[0].value];
            } else {
              value = el.type === 'radio' ? selected[0].value : true;
            }
          } else if (selected.length > 1) {
            value = Array.from(selected).map((input) => input.value);
          } else if (el.type === "radio") {
            const label = el
              .closest(".fieldbox")
              ?.querySelector("label")?.innerText;
            if (label) mandatory.push(label);
          } else {
            value = isCheckboxGroup ? [] : false;
          }
        } else if (el.dataset.type === "date") {
          value = format.dateDB(el.value);
        } else if (el.dataset.type === "json") {
          try {
            value = JSON.parse(el.value);
          } catch {
            value = el.value;
          }
        } else if (
          el.dataset.type === "integer" ||
          el.dataset.type === "number" ||
          el.dataset.type === "currency"
        ) {
          value = format.numberDB(el.value);
        } else if (el.dataset.masked === "true") {
          value = el.getUnmasked().value || '';
          if (el.dataset.type === "phone") {
            const isNational = el.dataset.national === "true";
            const scope = el.closest('.fieldbox') || form;
            const ddiInput = scope?.querySelector(`[name="${el.name}-ddi"]`) || form.querySelector(`[name="${el.name}-ddi"]`);
            const ddiRaw = ddiInput?.value || el.dataset.ddi || '';
            const ddiDigits = ddiRaw.replace(/\D/g, '');
            let digits = value.replace(/\D/g, '');
            if (!isNational && ddiDigits && digits.startsWith(ddiDigits)) {
              digits = digits.slice(ddiDigits.length);
            }
            value = digits.length > 0 ? `${ddiDigits}${digits}` : '';
          }
        } else {
          value = el.value;
        }

        if (el.required === true) {
          if (!value || value.length === 0) {
            mandatory.push(
              el.closest(".fieldbox").querySelector("label").innerText
            );
            el.data("valid", false);
            el.closest(".fieldbox").classList.add("invalid");
            el.closest(".fieldbox").classList.remove("valid");
            errors.push(el.name);
          } else {
            mandatory = mandatory.filter((item) => item !== el.name);
            el.data("valid", true);
            el.closest(".fieldbox").classList.remove("invalid");
            processed.push(el.name);
            data.push({ name: el.name, value });
          }
        } else {
          processed.push(el.name);
          data.push({ name: el.name, value });
        }
      });
    }

    const requiredMultiLists = this.#collectRequiredMultiListFields(options.groups || []);
    for (const field of requiredMultiLists) {
      const elem = this.#resolveElementById(form, field.id);
      const hasSelection = this.#multiListHasSelection(elem);
      const label = this.#resolveMultiListLabel(field, elem);

      if (!hasSelection) {
        if (label && !mandatory.includes(label)) mandatory.push(label);
        if (field.id && !errors.includes(field.id)) errors.push(field.id);
        this.#setMultiListValidationState(elem, false);
      } else {
        this.#setMultiListValidationState(elem, true);
      }
    }

    let msg = "";

    if (mandatory.length > 0) {
      msg +=
        '<p>Os seguintes campos são obrigatórios:</p><br><ul style="padding-left:2rem">';
      for (const i in mandatory) {
        msg += `<li>${mandatory[i]}</li>`;
      }
      msg += "</ul><br>";
    }

    let invalid;
    if (errors.length === 0) {
      invalid = form.querySelectorAll('[data-valid="false"]:not(:disabled)');
    } else {
      const exclusion = errors
        .flatMap((name) => [`[name="${name}"]`, `[id="${name}"]`])
        .join(", ");
      invalid = form.querySelectorAll(`[data-valid="false"]:not(:disabled):not(${exclusion})`);
    }

    if (invalid.length > 0) {
      msg +=
        '<p>Os seguintes campos estão com dados inválidos:</p><br><ul style="padding-left:2rem">';
      invalid.forEach((el) => {
        const error = el.closest(".fieldbox").querySelector("label").innerText;
        msg += `<li>${error}</li>`;
      });
      msg += "</ul><br>";
    }

    if (msg.length > 0) {
      msgbox({ type: "warning", message: msg });
      return false;
    }

    // Confirmação prévia declarada no config (`saveConfirmation.method`): o backend cota a alteração SEM gravar e o usuário confirma antes do `save`.
    const decision = await this.#confirmBeforeSave(e, options, data);
    if (!decision.proceed) return decision.result || { status: "cancelled" };
    if (decision.token) data.push({ name: "confirmation_token", value: decision.token });
    options.data = data;
    options.multiList = this.#collectMultiListData(form, options);
    let result = null;
    if (typeof options?.saveHandler === "function") {
      result = await options.saveHandler({
        data,
        options,
        form,
        event: e,
      });
    }
    if (!result || typeof result !== "object") {
      try {
        result = await this.#post(options, "save", options);
      } catch (error) {
        const payload = error?.payload && typeof error.payload === "object"
          ? error.payload
          : {};
        result = {
          status: "error",
          code: payload.code || error?.code || null,
          error: payload.error || null,
          message: payload.message || error?.message || null,
          payload,
        };
      }
    }
    let msgObj;
    /** Fecha/retorna o resultado bem-sucedido após feedback visual. */
    const fn = () => {
      if (options.isDialog && options.keepOpenOnSuccess !== true) {
        const dialog = e.target?.closest?.(".dialog")?.instance;
        dialog?.close();
      }

      if (typeof options.callback === "function") {
        const record =
          (Array.isArray(result?.data) && result.data.length > 0)
            ? result.data[0]
            : null;
        options.callback(record);
      }
    };

    if (result?.status === "success") {
      const savedRecord =
        (Array.isArray(result?.data) && result.data.length > 0)
          ? result.data[0]
          : null;
      const flushResult = await this.#flushNestedDatagrids(form, savedRecord);
      if (flushResult?.status !== "success") {
        result = flushResult;
      }
    }

    if (result?.status === "success") {
      if (result.rows === 1) {
        msgObj = {
          type: "success",
          message: dic?.message?.successRecordOne || "Registro salvo com sucesso."
        };
      } else {
        msgObj = {
          type: "success",
          message: dic?.message?.successRecords || "Registros salvos com sucesso."
        };
      }
    } else {
      const backendMessage = this.#resolveBackendErrorMessage(result, options);
      msgObj = {
        type: "error",
        message: backendMessage || dic?.message?.errorOperation || "Não foi possível concluir a operação."
      };
    }

    if (result?.status !== "success") {
      msgbox({
        type: resolveBackendErrorType(result),
        message: msgObj.message,
      });
      return result;
    }

    if (options?.successToast !== false) {
      toast(msgObj);
    }
    if (result?.status === "success") {
      options.__resetDirty?.();
      fn();
    }
    return result;
  }

  /**
   * Confirmação prévia de salvamento, declarada em `options.saveConfirmation = { method }` (método do próprio módulo que COTA a alteração sem gravá-la).
   * Resposta do método: `{ unchanged: true }` (nada a salvar: fecha sem confirmar), `{ confirmation }` (modelo exibido ao usuário: título, mensagem, colunas, linhas, resumo e
   * botões já traduzidos, mais o `token` da cotação) ou erro. Cancelar não persiste nada; confirmar devolve o `token`, que vai no `save` como `confirmation_token`.
   * Bloqueia duplo clique enquanto a cotação ou a confirmação estão em andamento.
   *
   * @param {Event} e Evento do botão Salvar.
   * @param {Object} options Opções do formulário.
   * @param {Array<{name:string,value:*}>} data Campos do formulário.
   * @returns {Promise<{proceed:boolean, token?:string, result?:Object}>}
   */
  async #confirmBeforeSave(e, options, data) {
    const method = String(options?.saveConfirmation?.method || "").trim();
    if (!method) return { proceed: true };
    if (options.__confirmingSave) return { proceed: false, result: { status: "cancelled" } };
    options.__confirmingSave = true;
    try {
      let quote;
      try {
        quote = await this.#post(options, method, { key: options.key || "main", data });
      } catch (error) {
        const payload = error?.payload && typeof error.payload === "object" ? error.payload : {};
        quote = { status: "error", code: payload.code || error?.code || null, error: payload.error || null, message: payload.message || error?.message || null, payload };
      }
      if (quote?.status !== "success") {
        msgbox({ type: resolveBackendErrorType(quote), message: this.#resolveBackendErrorMessage(quote, options) || dic?.message?.errorOperation || "Não foi possível concluir a operação." });
        return { proceed: false, result: quote };
      }
      if (quote.unchanged === true) {
        if (options.isDialog && options.keepOpenOnSuccess !== true) e.target?.closest?.(".dialog")?.instance?.close();
        return { proceed: false, result: { status: "success", rows: 0, unchanged: true } };
      }
      const confirmation = quote.confirmation;
      if (!confirmation || !confirmation.token) {
        msgbox({ type: "error", message: dic?.message?.errorOperation || "Não foi possível concluir a operação." });
        return { proceed: false, result: { status: "error" } };
      }
      const approved = await this.#showSaveConfirmation(confirmation);
      return approved ? { proceed: true, token: confirmation.token } : { proceed: false, result: { status: "cancelled" } };
    } finally {
      options.__confirmingSave = false;
    }
  }

  /**
   * Janela de confirmação (msgbox do EP) com a comparação atual x nova. Resolve `true` somente no botão de confirmar; Cancelar, Esc e fechar resolvem `false`.
   *
   * @param {Object} confirmation Modelo vindo do backend (textos já traduzidos).
   * @returns {Promise<boolean>}
   */
  #showSaveConfirmation(confirmation) {
    const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
    const rows = Array.isArray(confirmation.rows) ? confirmation.rows : [];
    const summary = confirmation.summary && typeof confirmation.summary === "object" ? confirmation.summary : null;
    const html = [
      `<div class="autoform-confirmation-content">`,
      confirmation.message ? `<p class="autoform-confirmation-message">${escapeHtml(confirmation.message)}</p>` : "",
      `<table class="autoform-confirmation-table"><thead><tr><th></th><th>${escapeHtml(confirmation.columns?.current)}</th><th>${escapeHtml(confirmation.columns?.next)}</th></tr></thead><tbody>`,
      ...rows.map((row) => `<tr><th scope="row">${escapeHtml(row.label)}</th><td>${escapeHtml(row.current)}</td><td>${escapeHtml(row.next)}</td></tr>`),
      `</tbody></table>`,
      confirmation.warning ? `<p class="autoform-confirmation-warning">${escapeHtml(confirmation.warning)}</p>` : "",
      summary ? `<p class="autoform-confirmation-summary autoform-confirmation-${escapeHtml(summary.tone || "neutral")}"><span>${escapeHtml(summary.label)}</span> <strong>${escapeHtml(summary.value)}</strong>${summary.note ? ` <small>${escapeHtml(summary.note)}</small>` : ""}</p>` : "",
      `</div>`,
    ].join("");
    return new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      msgbox({
        title: confirmation.title,
        message: html,
        class: "autoform-confirmation",
        buttons: [
          { text: confirmation.cancelText || dic?.labels?.cancel, data: { button: "esc" }, color: "red", click: () => finish(false) },
          {
            text: confirmation.confirmText || dic?.labels?.ok,
            data: { button: "enter" },
            color: "green",
            click: (event) => {
              if (settled) return;
              const button = event?.currentTarget || event?.target;
              if (button) button.disabled = true;
              finish(true);
            },
          },
        ],
        onClose: () => finish(false),
      });
    });
  }

  /**
   * API pública de save, delegando ao fluxo interno validado.
   *
   * @param {Event} e Evento.
   * @param {Object} options Opções; veja {@link AutoForm#autoform}.
   * @returns {Promise<Object|false>} Resultado.
   */
  async save(e, options) {
    return this.#save(e, options);
  }

  /**
   * API pública de cancelamento.
   *
   * @param {Event} e Evento.
   * @param {Object} options Opções; veja {@link AutoForm#autoform}.
   * @returns {void}
   */
  cancel(e, options) {
    return this.#cancel(e, options);
  }
}

const autoForm = new AutoForm();

/**
 * Factory declarativa do AutoForm.
 *
 * @param {Object} options Configuração; veja {@link AutoForm#autoform}.
 * @returns {Promise<void>} Renderização concluída.
 *
 * @example
 * await create({
 *   element: 'autoform',
 *   parent: 'main',
 *   scope: 'product',
 *   product: 'platform',
 *   service: 'accounts',
 *   domain: 'tenancy',
 *   module: 'tenants',
 *   key: 'main',
 *   mode: 'edit',
 *   isDialog: true,
 *   filter: {
 *     conditions: [{ key: 'id', operator: '=', value: tenantId }]
 *   }
 * });
 *
 * @see AutoForm#autoform
 * @see normalizeServiceTarget
 * @see module:components/create/element
 */
const autoform = (options) => autoForm.autoform(options);

/**
 * Serializa dados do AutoForm sem salvar.
 *
 * @param {Event} e Evento/contexto.
 * @param {Object} options Opções; veja {@link AutoForm#autoform}.
 * @returns {Promise<*>} Dados ou retorno via callback.
 * @see AutoForm#getDataFields
 */
const getDataFields = (e, options) => autoForm.getDataFields(e, options);

/**
 * Salva um AutoForm existente.
 *
 * @param {Event} e Evento.
 * @param {Object} options Opções; veja {@link AutoForm#autoform}.
 * @returns {Promise<Object|false>} Resultado.
 * @see AutoForm#save
 */
const saveForm = (e, options) => autoForm.save(e, options);

/**
 * Cancela/fecha um AutoForm existente.
 *
 * @param {Event} e Evento.
 * @param {Object} options Opções; veja {@link AutoForm#autoform}.
 * @returns {void}
 * @see AutoForm#cancel
 */
const cancelForm = (e, options) => autoForm.cancel(e, options);
export default autoform;
export { getDataFields, saveForm, cancelForm };
